import React, { useMemo, useRef, useState } from 'react';
import Link from 'components/link';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';

import type { NearbyCity } from '../nearby-cities-pills';

export interface AreaSwitcherProps {
  areaName: string;
  nearbyCities?: readonly NearbyCity[];
}

/**
 * Large area-name pill matching the design's snapshot-view header. When
 * nearby areas are available it opens a searchable panel that links
 * straight to each area's own city-info page — otherwise it's a plain,
 * non-interactive title.
 */
const AreaSwitcher = ({ areaName, nearbyCities = [] }: AreaSwitcherProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);

  const filtered = useMemo(
    () => nearbyCities.filter(city => city.name.toLowerCase().includes(query.trim().toLowerCase())),
    [nearbyCities, query]
  );

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  if (nearbyCities.length === 0) {
    return (
      <div className={styles.pillStatic}>
        <PlaceOutlined className={styles.pinIcon} />
        <span>{areaName}</span>
      </div>
    );
  }

  return (
    <div className={styles.component}>
      <button
        ref={triggerRef}
        className={styles.pill}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <PlaceOutlined className={styles.pinIcon} />
        <span>{areaName}</span>
        <KeyboardArrowDown className={buildClassName(styles.chevron, open && styles.chevronOpen)} />
      </button>

      {open && (
        <>
          <div className={styles.backdrop} onClick={close} />
          <div className={styles.panel} onKeyDown={e => { if (e.key === 'Escape') close(); }}>
            <div className={styles.searchWrap}>
              <SearchOutlined className={styles.searchIcon} />
              <input
                autoFocus
                className={styles.searchInput}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search a city or area…"
                aria-label="Search a city or area"
              />
            </div>
            <div className={styles.list}>
              {filtered.length > 0 ? filtered.map(city => (
                <Link key={city.slug} href={`/${city.slug}-info`} className={styles.item}>
                  <span>{city.name}</span>
                </Link>
              )) : (
                <div className={styles.empty} role="status" aria-live="polite">No areas found</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AreaSwitcher;
